import { useState } from 'react';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { PersonRef } from '../../types/induction';
import { personLabel } from './inductionLabels';

export interface BulkEntry {
  ref: PersonRef;
  name: string | null;
  inductions: { id: number; title: string }[];
}

interface Props {
  entries: BulkEntry[];
  onClose: () => void;
  onDone: (result: { records: number; people: number }) => void;
}

type BulkBasis = 'prior_competency' | 'assessed';

/**
 * Confirm a bulk sign-off from the register. The roll-out case — "everyone who
 * already drives the tractor" — is recorded as recognised prior competency, so
 * the register says honestly that nobody watched them do it today.
 */
export default function BulkSignOffDialog({ entries, onClose, onDone }: Props) {
  const [basis, setBasis] = useState<BulkBasis>('prior_competency');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const total = entries.reduce((n, e) => n + e.inductions.length, 0);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await inductionService.signOffBulk(
        entries.map(e => ({ ref: e.ref, induction_ids: e.inductions.map(i => i.id) })),
        notes.trim() || undefined,
        basis,
      );
      onDone(result ?? { records: total, people: entries.length });
    } catch (e) {
      setError(errorMessage(e, 'Could not record the sign-offs'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title="Record sign-offs"
      onClose={onClose}
      onSubmit={submit}
      submitLabel={`Record ${total} sign-off${total === 1 ? '' : 's'}`}
      submitting={saving}
      submitDisabled={total === 0}
      error={error}
    >
      <p className="text-sm text-neutral-600 break-words">
        {total} sign-off{total === 1 ? '' : 's'} for {entries.length} {entries.length === 1 ? 'person' : 'people'}.
      </p>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-neutral-700 mb-1">How do you know they are competent?</legend>
        <label className="flex items-start gap-2 text-sm min-w-0">
          <input type="radio" name="bulk-basis" className="mt-1 shrink-0" checked={basis === 'prior_competency'}
            onChange={() => setBasis('prior_competency')} />
          <span className="min-w-0 break-words">
            <span className="font-medium text-neutral-900">Existing operator — recognised</span>
            <span className="block text-neutral-500">They already run this safely. Use this when recording your current operators.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm min-w-0">
          <input type="radio" name="bulk-basis" className="mt-1 shrink-0" checked={basis === 'assessed'}
            onChange={() => setBasis('assessed')} />
          <span className="min-w-0 break-words">
            <span className="font-medium text-neutral-900">Assessed</span>
            <span className="block text-neutral-500">You watched each of them operate it and went through the induction.</span>
          </span>
        </label>
      </fieldset>
      <Field label="Note" htmlFor="bulk-notes" hint="Kept on every record, e.g. “Operators before inductions started, Sept 2026”.">
        <textarea id="bulk-notes" className={inputClass} rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
      <div className="rounded-lg border border-neutral-200 max-h-48 overflow-y-auto divide-y divide-neutral-100">
        {entries.map(e => (
          <div key={e.ref} className="px-3 py-2 text-sm min-w-0">
            <div className="font-medium text-neutral-900 break-words">{personLabel(e.name)}</div>
            <div className="text-neutral-500 break-words">{e.inductions.map(i => i.title).join(', ')}</div>
          </div>
        ))}
      </div>
    </FormModal>
  );
}
